import { motion, useReducedMotion } from 'framer-motion'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import styled from 'styled-components'

const isImage = (file: File) => file.type?.split('/')[0] === 'image'

export const ToastImagePreview: React.FC<{ file: File }> = ({ file }) => {
  const previewUrl = useMemo(() => (isImage(file) ? URL.createObjectURL(file) : null), [file])

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    },
    [previewUrl]
  )

  if (!previewUrl) return null
  return <ImagePreview $src={previewUrl} />
}

export const ToastFilePreview: React.FC<{
  file: File
  progress?: number
  onCancel?: () => void
  detail?: string
}> = ({ file, progress, onCancel, detail }) => {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const scale = Math.min(1, Math.max(0, progress ?? 0))

  return (
    <Column>
      <FileName>{file.name}</FileName>
      {detail && <Detail>{detail}</Detail>}
      {progress != null && (
        <ProgressRow>
          <ProgressTrack>
            <ProgressFill
              initial={false}
              animate={{ scaleX: scale }}
              transition={{ duration: reduceMotion ? 0 : 0.35, ease: 'easeOut' }}
            />
          </ProgressTrack>
          {onCancel && (
            <CancelButton
              type="button"
              aria-label={t('files.upload.cancel')}
              onClick={event => {
                event.stopPropagation()
                onCancel()
              }}
            >
              <FaTimes size={11} />
            </CancelButton>
          )}
        </ProgressRow>
      )}
    </Column>
  )
}

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const FileName = styled.div`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 6px 0 4px;
`

const ProgressTrack = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 999px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutralWhite}33;
`

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 16px;
  height: 16px;
  margin-left: ${({ theme }) => theme.spacing.xs}rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutralWhite};
  opacity: 0.75;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`

const ProgressFill = styled(motion.div)`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  transform-origin: left center;
`

const Detail = styled.div`
  max-width: 100%;
  margin: 6px 0 4px;
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.3;
  overflow-wrap: anywhere;
  white-space: normal;
`

const ImagePreview = styled.div<{ $src: string }>`
  flex: none;
  align-self: center;
  margin-left: ${({ theme }) => theme.spacing.sm}rem;
  width: 32px;
  height: 32px;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 5px;
`
